import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const MemberNotificationCard = ({ member, settings, onUpdate, onTest }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const reminderOptions = [
    { value: '5min', label: '5 minutes before' },
    { value: '15min', label: '15 minutes before' },
    { value: '30min', label: '30 minutes before' },
    { value: '1hour', label: '1 hour before' },
    { value: '2hours', label: '2 hours before' },
    { value: '1day', label: '1 day before' },
    { value: '2days', label: '2 days before' },
    { value: '1week', label: '1 week before' }
  ];

  const deliveryMethodOptions = [
    { value: 'push', label: 'Push Notification' },
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'normal', label: 'Normal Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const toggleReminder = (category, reminder) => {
    const currentReminders = settings?.[category]?.reminders || [];
    const newReminders = currentReminders?.includes(reminder)
      ? currentReminders?.filter(r => r !== reminder)
      : [...currentReminders, reminder];
    
    onUpdate({
      [category]: {
        ...settings?.[category],
        reminders: newReminders
      }
    });
  };

  const toggleDeliveryMethod = (method) => {
    const currentMethods = settings?.deliveryMethod || [];
    const newMethods = currentMethods?.includes(method)
      ? currentMethods?.filter(m => m !== method)
      : [...currentMethods, method];
    
    onUpdate({ deliveryMethod: newMethods });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Member Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ borderLeft: `4px solid ${member?.color}` }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
            style={{ backgroundColor: member?.color }}
          >
            {member?.initials}
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-lg">{member?.name}</h3>
            <p className="text-sm text-muted-foreground">{member?.role}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="bell" size={16} />
            <span>
              {settings?.enabled ? 'Notifications On' : 'Notifications Off'}
            </span>
          </div>
          
          <button
            onClick={(e) => {
              e?.stopPropagation();
              onUpdate({ enabled: !settings?.enabled });
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings?.enabled ? 'bg-primary' : 'bg-muted'
            }`}
            aria-label="Toggle notifications"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings?.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>

          <Icon
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            size={20}
            className="text-muted-foreground"
          />
        </div>
      </div>

      {/* Expanded Settings */}
      {isExpanded && (
        <div className="p-6 space-y-6 bg-muted/10">
          {/* Messaging Alerts */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="message-square" size={20} style={{ color: member?.color }} />
                <h4 className="font-semibold text-foreground">Messaging Alerts</h4>
              </div>
              <button
                onClick={() => onUpdate({
                  messaging: { ...settings?.messaging, enabled: !settings?.messaging?.enabled }
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings?.messaging?.enabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings?.messaging?.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {settings?.messaging?.enabled && (
              <div className="space-y-3 ml-7">
                <Checkbox
                  checked={settings?.messaging?.sound}
                  onCheckedChange={(checked) => onUpdate({
                    messaging: { ...settings?.messaging, sound: checked }
                  })}
                  label="Sound"
                />
                <Checkbox
                  checked={settings?.messaging?.vibration}
                  onCheckedChange={(checked) => onUpdate({
                    messaging: { ...settings?.messaging, vibration: checked }
                  })}
                  label="Vibration"
                />
                <Checkbox
                  checked={settings?.messaging?.preview}
                  onCheckedChange={(checked) => onUpdate({
                    messaging: { ...settings?.messaging, preview: checked }
                  })}
                  label="Message Preview"
                />
              </div>
            )}
          </div>

          {/* Task Reminders */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="check-square" size={20} style={{ color: member?.color }} />
                <h4 className="font-semibold text-foreground">Task Reminders</h4>
              </div>
              <button
                onClick={() => onUpdate({
                  tasks: { ...settings?.tasks, enabled: !settings?.tasks?.enabled }
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings?.tasks?.enabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings?.tasks?.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {settings?.tasks?.enabled && (
              <div className="space-y-4 ml-7">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Reminder Times
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {reminderOptions?.map(option => (
                      <label
                        key={option?.value}
                        className="flex items-center gap-2 p-2 rounded border border-border hover:bg-muted/30 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={settings?.tasks?.reminders?.includes(option?.value)}
                          onCheckedChange={() => toggleReminder('tasks', option?.value)}
                        />
                        <span className="text-sm text-foreground">{option?.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Checkbox
                  checked={settings?.tasks?.sound}
                  onCheckedChange={(checked) => onUpdate({
                    tasks: { ...settings?.tasks, sound: checked }
                  })}
                  label="Sound"
                />
                <Checkbox
                  checked={settings?.tasks?.vibration}
                  onCheckedChange={(checked) => onUpdate({
                    tasks: { ...settings?.tasks, vibration: checked }
                  })}
                  label="Vibration"
                />
              </div>
            )}
          </div>

          {/* Calendar Event Notifications */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="calendar" size={20} style={{ color: member?.color }} />
                <h4 className="font-semibold text-foreground">Calendar Events</h4>
              </div>
              <button
                onClick={() => onUpdate({
                  calendar: { ...settings?.calendar, enabled: !settings?.calendar?.enabled }
                })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings?.calendar?.enabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings?.calendar?.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {settings?.calendar?.enabled && (
              <div className="space-y-4 ml-7">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Reminder Times
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {reminderOptions?.map(option => (
                      <label
                        key={option?.value}
                        className="flex items-center gap-2 p-2 rounded border border-border hover:bg-muted/30 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={settings?.calendar?.reminders?.includes(option?.value)}
                          onCheckedChange={() => toggleReminder('calendar', option?.value)}
                        />
                        <span className="text-sm text-foreground">{option?.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Checkbox
                  checked={settings?.calendar?.sound}
                  onCheckedChange={(checked) => onUpdate({
                    calendar: { ...settings?.calendar, sound: checked }
                  })}
                  label="Sound"
                />
                <Checkbox
                  checked={settings?.calendar?.vibration}
                  onCheckedChange={(checked) => onUpdate({
                    calendar: { ...settings?.calendar, vibration: checked }
                  })}
                  label="Vibration"
                />
              </div>
            )}
          </div>

          {/* Delivery Preferences */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="send" size={20} style={{ color: member?.color }} />
              <h4 className="font-semibold text-foreground">Delivery Preferences</h4>
            </div>
            
            <div className="space-y-4 ml-7">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Delivery Methods
                </label>
                <div className="space-y-2">
                  {deliveryMethodOptions?.map(option => (
                    <label
                      key={option?.value}
                      className="flex items-center gap-2 p-2 rounded border border-border hover:bg-muted/30 cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={settings?.deliveryMethod?.includes(option?.value)}
                        onCheckedChange={() => toggleDeliveryMethod(option?.value)}
                      />
                      <span className="text-sm text-foreground">{option?.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Select
                label="Priority Level"
                value={settings?.priority}
                onChange={(value) => onUpdate({ priority: value })}
                options={priorityOptions}
              />

              <Checkbox
                checked={settings?.quietHoursEnabled}
                onCheckedChange={(checked) => onUpdate({ quietHoursEnabled: checked })}
                label="Respect Quiet Hours"
                description="Follow global quiet hours settings"
              />
            </div>
          </div>

          {/* Test Notification */}
          <div className="flex justify-end pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={onTest}
            >
              <Icon name="bell" size={16} className="mr-2" />
              Test Notification
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberNotificationCard;